import React from 'react';
import { createPortal } from 'react-dom';
import { getFloatingMenuStyle, type FloatingMenuAlign } from '../../lib/floatingMenu';

export type MenuButtonItem = {
  key: string;
  label: string;
  /** A second line under the label. */
  description?: string;
  onSelect: () => void;
  disabled?: boolean;
  title?: string;
  /** Set for an on/off option: the item becomes a menu checkbox and shows a check while on. */
  checked?: boolean;
};

const ITEM_SELECTOR = '[role="menuitem"]:not(:disabled), [role="menuitemcheckbox"]:not(:disabled)';

type MenuButtonProps = {
  /** The button's content (text, or ⋯ for an overflow menu). */
  children: React.ReactNode;
  items: MenuButtonItem[];
  className?: string;
  /** Required when the button shows only an icon or ⋯. */
  ariaLabel?: string;
  menuLabel: string;
  align?: FloatingMenuAlign;
  disabled?: boolean;
};

/**
 * A button that opens a short menu (New questionnaire › Crew report / Customer survey; Add closure › Holiday / Dark
 * day; the questionnaire editor's ⋯). The menu is portalled to the body so a scrolling toolbar can't clip it. Arrow
 * keys, Home and End move between items; Escape and Tab close it and give focus back to the button.
 */
export default function MenuButton({ children, items, className = 'btn', ariaLabel, menuLabel, align = 'left', disabled }: MenuButtonProps) {
  const [open, setOpen] = React.useState(false);
  const [style, setStyle] = React.useState<React.CSSProperties | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const menuId = React.useId();

  React.useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target || menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleViewportChange = () => setOpen(false);
    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', handleViewportChange);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [open]);

  React.useLayoutEffect(() => {
    if (!open) return;
    // The first item that can run, or the menu itself (so Escape still closes it) when none can.
    (menuRef.current?.querySelector<HTMLElement>(ITEM_SELECTOR) ?? menuRef.current)?.focus();
  }, [open, style]);

  const openMenu = () => {
    setStyle(getFloatingMenuStyle(buttonRef.current, 240, align));
    setOpen(true);
  };

  const closeMenu = (restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeMenu(true);
      return;
    }
    if (event.key === 'Tab') {
      closeMenu(true);
      return;
    }
    const nodes = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
    if (!nodes.length || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const index = nodes.indexOf(document.activeElement as HTMLElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? nodes.length - 1
      : (index + (event.key === 'ArrowDown' ? 1 : -1) + nodes.length) % nodes.length;
    nodes[next]?.focus();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? closeMenu(false) : openMenu())}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            event.preventDefault();
            event.stopPropagation();
            closeMenu(false);
            return;
          }
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          openMenu();
        }}
      >
        {children}
      </button>
      {open && style && typeof document !== 'undefined' ? createPortal(
        <div ref={menuRef} id={menuId} className="dropdown-menu pq-menu" role="menu" tabIndex={-1} aria-label={menuLabel} style={style} onKeyDown={handleMenuKeyDown}>
          <div className="dropdown-menu__section">
            {items.map(item => (
              <button
                key={item.key}
                type="button"
                role={item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
                aria-checked={item.checked}
                tabIndex={-1}
                className="dropdown-menu__item"
                disabled={item.disabled}
                title={item.title}
                onClick={() => {
                  closeMenu(true);
                  item.onSelect();
                }}
              >
                {item.checked === undefined ? null : (
                  <span className="dropdown-menu__check" aria-hidden="true">{item.checked ? '✓' : ''}</span>
                )}
                {item.description ? (
                  <span className="dropdown-menu__item-text"><span>{item.label}</span><small>{item.description}</small></span>
                ) : item.label}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
